import {clear, get, set, keys, Store} from 'idb-keyval'
import {BlankState, StatePartTypes} from '../store/stateParts'
import StateValidation from '../store/stateValidation'
import {StateChanges} from '../store/stateChanges'
import {DocumentManager} from '@/DocumentManager'
import store from '../store'
import {AppConfig} from '../appConfig'

import {getGitCommitId} from 'shared/js/getGitCommitId'
export const LATEST_VERSION = '2.0 - git commit: ' + getGitCommitId()

export const createSubset = (obj, ...keys) =>
  keys.reduce((a, c) => ({ ...a, [c]: obj[c] }), {})

const SavedStateName = 'abbreviation-expander-saved-state'

const DROPBOX_DATA = 'dropboxData'

const customKeyVal = new Store(SavedStateName, SavedStateName)

export const StateSaver = {
  saveState: async () => {
    try {
      for (const statePartType of Object.keys(StatePartTypes)) {
        if (statePartType !== 'never-save') {
          const savedStatePartKey = SavedStateName + '-' + statePartType
          const statePartString = statePartType.includes('document') ? DocumentManager.save()
            : JSON.stringify(Object.assign(createSubset(store.state, ...(StatePartTypes[statePartType])),
                                            { version: LATEST_VERSION}))
          await set(savedStatePartKey, statePartString, customKeyVal)
        }
      }
      await set(DROPBOX_DATA, store.state.dropbox, customKeyVal)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e)
      await clear(customKeyVal)
      throw "Couldn't save to IndexedDB"
    }
  }
}
let loadingPromiseResolver
export const loadingPromise = new Promise(resolve => {
  loadingPromiseResolver = resolve
})
export async function loadLocalData(isResultsPage) {
  try {
    if (await loadDocumentState(store, isResultsPage)) {
      // don't try loading Dropbox data unless the document loaded successfully
      const dropboxConnectionData = await get(DROPBOX_DATA, customKeyVal)
      if (AppConfig.OFFER_DROPBOX) {
        store.commit('SET_DROPBOX_STATUS', dropboxConnectionData?.status || null)
        store.commit('SET_PROJECT_SETTINGS', dropboxConnectionData?.projectSettings || null)
      }
    }
  } finally {
    loadingPromiseResolver()
  }
}

function buildUpValidationResult(old, latest) {
  var builtUp
  if (old !== undefined) builtUp = Object.assign({}, old)
  if (latest !== undefined) {
    if (builtUp !== undefined) { // i.e. they're both OBJECTS
      builtUp.success = builtUp.success && latest.success
      builtUp.summary = builtUp.success ? 'success' : 'failure' // back. compat.
      builtUp.failures = builtUp.failures.concat(latest.failures)
      builtUp.errorData = builtUp.errorData.split(', ').concat([latest.errorData]).join(', ')
      const oldSavedStateStringKeys = Object.keys(builtUp).filter(k => k.startsWith('savedStateString'))
      builtUp['savedStateString' + (oldSavedStateStringKeys.length + 1)] = latest.savedStateString
    } else builtUp = Object.assign({}, latest)
  }
  return builtUp
}

const nakdanSavedState = SavedStateName

async function loadDocumentState({commit, state}, isResultsPage) {
  var workingState = {}
  var workingValidationResult, savedStateVersion
  let documentLoaded = false
  const savedStatePartKeys = (await keys(customKeyVal))
    .filter(k => k.startsWith(nakdanSavedState) && k !== nakdanSavedState)
  for (const savedStatePartKey of savedStatePartKeys) {
    const savedStateString = await get(savedStatePartKey, customKeyVal)
    if (savedStatePartKey.includes('document')) {
      if (isResultsPage) {
        const documentLoadValidationResult = DocumentManager.load(savedStateString)
        documentLoaded = true
        workingValidationResult = buildUpValidationResult(workingValidationResult, documentLoadValidationResult)
      }
    } else {
      var savedStatePart
      let parseError = null
      try {
        savedStatePart = JSON.parse(savedStateString)
      } catch (error) {
        parseError = error
      }
      if (parseError || typeof savedStatePart !== 'object' || savedStatePart === null) {
        workingValidationResult = buildUpValidationResult(workingValidationResult, {
          summary: 'failure', success: false, savedStateString: savedStateString, failures: [{
            failureType: 'invalid ' + savedStatePartKey,
            errorData: parseError ? 'JSON parse error' : "not an 'object'"
          }]
        })
      } else {
        var partTypesArray = StatePartTypes[savedStatePartKey.slice(nakdanSavedState.length + 1)] // past connecting '-' (dash)
        if (!partTypesArray) partTypesArray = (savedStatePartKeys.length > 1) ? []
          : Object.keys(savedStatePart) // backward compatibility
        Object.assign(workingState, createSubset(savedStatePart, ...partTypesArray))
        if (Object.prototype.hasOwnProperty.call(savedStatePart, 'version')) {
          savedStateVersion = (savedStateVersion > savedStatePart.version) ? savedStateVersion
                                                                           : savedStatePart.version
        }
      }
    }
  }
  if (savedStatePartKeys.length) workingState.firstTimeInitialized = false
  const preview = Object.assign({}, JSON.parse(JSON.stringify(state)), // in case something's "never-saved" but IS tested
                                workingState, (savedStateVersion ? { version: savedStateVersion } : null))
  const fullValidationResult = StateValidation.validateState(preview) // note: validation "fixes" not done now!
  if (fullValidationResult && fullValidationResult.summary === 'failure') {
    documentLoaded = false
    fullValidationResult['success'] = false // until we change it in the 'StateValidation' file
    fullValidationResult['savedStateString'] = '***(full state validation)***'
    // eslint-disable-next-line no-console
    console.log('Full state validation results: ' + JSON.stringify(fullValidationResult))
    workingValidationResult = buildUpValidationResult(workingValidationResult, fullValidationResult)
  }
  var stateToCommit = workingState
  if (workingValidationResult) {
    if (!(workingValidationResult.success)) {
      documentLoaded = false
      stateToCommit = JSON.parse(JSON.stringify(BlankState))
    }
    workingValidationResult['popupShown'] = false
  }
  stateToCommit['validationResult'] = workingValidationResult
  commit(StateChanges.BUILD_STATE, {
    contextData: { wordIndex: 0, letterIndex: 0 }, // for undo
    newStateJson: JSON.stringify(stateToCommit),
    calledBy: 'Initialize Application in localData.js'
  })
  return documentLoaded
}
